import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/Button'
import { Input, Label } from '@/components/ui/Input'
import { cleanTotp, isTotpCode } from '@/lib/secrets'

export interface TotpStepUpDialogProps {
  open: boolean
  title: string
  /** What exactly is being confirmed (group and field names, never values). */
  body?: string
  confirmLabel?: string
  danger?: boolean
  /** The request is in flight. */
  busy?: boolean
  /** The server's answer to the last code: wrong, already used, locked (429), … */
  error?: string | null
  onSubmit: (code: string) => void
  onCancel: () => void
}

/**
 * Step-up confirmation: the owner types the current 6-digit code from the authenticator app
 * for every change of integration keys (SPEC 1.5). A code works once, so the field is emptied
 * after every answer from the server. Modal, like Confirm (role=alertdialog); Esc cancels.
 * Presentational: the caller runs the request and passes `busy` / `error` (Storybook states).
 */
export function TotpStepUpDialog({
  open,
  title,
  body,
  confirmLabel = 'Подтвердить',
  danger = false,
  busy = false,
  error = null,
  onSubmit,
  onCancel,
}: TotpStepUpDialogProps) {
  const fieldId = useId()
  const [code, setCode] = useState('')
  const input = useRef<HTMLInputElement>(null)

  // A new dialog and every answer from the server (busy → idle) start with an empty field:
  // a code is single-use, the same one is never sent twice.
  useEffect(() => {
    if (busy) return
    setCode('')
    if (open) input.current?.focus()
  }, [open, busy, error])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, busy, onCancel])

  if (!open) return null
  const valid = isTotpCode(code)

  function submit(e: FormEvent) {
    e.preventDefault()
    if (valid && !busy) onSubmit(code)
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Отмена"
        className="absolute inset-0 bg-scrim"
        onClick={() => !busy && onCancel()}
      />
      <form
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="stepup-title"
        onSubmit={submit}
        className="relative max-h-full w-full max-w-sm overflow-y-auto rounded-md border border-hairline bg-surface-1 p-4 shadow-e3 sm:p-6"
      >
        <div id="stepup-title" className="text-title-2 font-semibold text-ink-1 [overflow-wrap:anywhere]">
          {title}
        </div>
        {body && <p className="mt-2 text-body text-ink-2 [overflow-wrap:anywhere]">{body}</p>}
        <div className="mt-4">
          <Label htmlFor={fieldId}>Код из приложения 2FA</Label>
          <Input
            id={fieldId}
            ref={input}
            name="totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="6 цифр"
            className="tnum tracking-widest"
            value={code}
            aria-invalid={error ? true : undefined}
            onChange={(e) => setCode(cleanTotp(e.target.value))}
            disabled={busy}
          />
          <p className="mt-1.5 text-caption text-ink-3">
            Каждый код действует один раз. Если код уже вводили при входе, дождитесь следующего.
          </p>
        </div>
        {error && (
          <p role="alert" className="mt-3 flex items-start gap-1.5 text-caption text-danger">
            <span aria-hidden>✕</span>
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </p>
        )}
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant={danger ? 'destructive' : 'primary'}
            loading={busy}
            disabled={!valid}
          >
            {confirmLabel}
          </Button>
        </div>
      </form>
    </div>
  )
}
